import EditDialogProvider, {
  useDialogProviderContext,
} from "src/features/DialogProvider";
import { useAlerting, useTranslation } from "src/hooks";
import Content from "./content";
import { useEffect, useState } from "react";
import {
  TableColumnKey,
  TableRow,
} from "src/views/WalletManagement/WalletBalanceMonitor/types/Table";
import useValidation from "./hooks/useValidation";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import {
  amountToCryptoAmount,
  findDecimalByChainNameAndAsset,
} from "src/utils/helper";
import { editWalletBalanceMonitor } from "src/api/merchant";
import EnumCriteria from "@wallet-manager/node-types/dist/src/postgres/const/Criteria";
import { GetListRes } from "src/views/WalletManagement/WalletBalanceMonitor/types/GetListRes";

export type EditFields = {
  thresholdLower: string;
  thresholdUpper: string;
};

function EditDialog({
  rowData,
}: {
  rowData: TableRow & { rawData: GetListRes };
}) {
  const initFields = {
    thresholdLower: rowData[TableColumnKey.ThresholdLower] || "",
    thresholdUpper: rowData[TableColumnKey.ThresholdUpper] || "",
  };

  const [fields, setFields] = useState<EditFields>(initFields);
  const { translatePrefix, refreshData } = usePageConfigProviderContext();
  const { t } = useTranslation(translatePrefix);
  const { validate } = useValidation(initFields);

  const { isOpen, setIsOpen } = useDialogProviderContext();

  const { alerting } = useAlerting();

  const { chain_type, chain_id } = rowData.rawData;

  useEffect(() => {
    if (isOpen) {
      setFields(initFields);
    }
  }, [isOpen]);

  const onConfirm = async () => {
    const isValid = validate(fields);

    if (!isValid || chain_type === undefined || chain_id === undefined) {
      return;
    }

    const decimal = findDecimalByChainNameAndAsset(
      String(chain_type),
      chain_id,
      rowData[TableColumnKey.Currency],
    );

    const getDetails = () => {
      const lowerThresholdDetail = fields.thresholdLower
        ? [
            {
              criteria: EnumCriteria.LessThan,
              thresholdValue: amountToCryptoAmount(
                fields.thresholdLower,
                Number(decimal),
              ),
            },
          ]
        : [];
      const upperThresholdDetail = fields.thresholdUpper
        ? [
            {
              criteria: EnumCriteria.GreaterThan,
              thresholdValue: amountToCryptoAmount(
                fields.thresholdUpper,
                Number(decimal),
              ),
            },
          ]
        : [];

      return [...lowerThresholdDetail, ...upperThresholdDetail];
    };

    const res = await editWalletBalanceMonitor({
      assetName: rowData[TableColumnKey.Currency],
      walletAddress: rowData[TableColumnKey.WalletAddress],
      details: getDetails(),
      chainType: chain_type,
      chainId: chain_id,
    });

    if (!res) {
      return;
    }

    alerting("success", t("edit_wallet_balance_monitor_success"));
    refreshData();
    setIsOpen(false);
  };

  const isLowerThresholdInvalid =
    Number(fields.thresholdLower) < 0 || isNaN(Number(fields.thresholdLower));
  const isUpperThresholdInvalid =
    fields.thresholdUpper !== "" &&
    (Number(fields.thresholdUpper) <= 0 ||
      isNaN(Number(fields.thresholdUpper)));

  const isConfirmDisabled =
    (!fields.thresholdLower && !fields.thresholdUpper) ||
    isLowerThresholdInvalid ||
    isUpperThresholdInvalid;

  return (
    <EditDialogProvider.DialogInOne
      title={t("edit_dialog_title")}
      content={
        <Content
          fields={fields}
          setFields={setFields}
          rowData={rowData}
          isLowerThresholdInvalid={isLowerThresholdInvalid}
          isUpperThresholdInvalid={isUpperThresholdInvalid}
        />
      }
      isConfirmDisabled={isConfirmDisabled}
      onConfirm={onConfirm}
      maxWidth="md"
    />
  );
}

export default EditDialog;
